import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAuth } from '../../contexts/AuthContext';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { handleNotificationTap } from '../../services/notificationRoute';
import type { ShowWhisperPayload } from '../../services/pushNotificationTypes';
import PushNotificationService from '../../services/pushNotificationService';
import { notifyNotificationsChanged } from '../../services/notificationsBus';
import { GlassCard } from '../ui/GlassCard';

/**
 * Wires the full notification pipeline: OS push tap, in-app banner while
 * foregrounded, and a refresh of the bell. Invite taps open the join sheet.
 */
export function NotificationHost() {
  const insets = useSafeAreaInsets();
  const { isAuthenticated } = useAuth();
  const [whisper, setWhisper] = useState<ShowWhisperPayload | null>(null);

  const dismissWhisper = useCallback(() => setWhisper(null), []);

  useEffect(() => {
    const service = PushNotificationService.getInstance();
    service.setOnNotificationTap((message) => {
      notifyNotificationsChanged();
      handleNotificationTap(message);
    });
    service.setShowWhisper((payload) => {
      notifyNotificationsChanged();
      setWhisper(payload);
    });
    return () => {
      service.setOnNotificationTap(() => {});
      service.setShowWhisper(() => {});
    };
  }, []);

  useEffect(() => {
    if (!whisper) return undefined;
    const id = setTimeout(() => setWhisper(null), 8000);
    return () => clearTimeout(id);
  }, [whisper]);

  useEffect(() => {
    if (!isAuthenticated) setWhisper(null);
  }, [isAuthenticated]);

  if (!whisper) return null;

  return (
    <View pointerEvents="box-none" style={[styles.wrap, { top: insets.top + spacing.sm }]}>
      <GlassCard
        accessibilityLabel={`${whisper.title}. ${whisper.message}`}
        onPress={() => {
          whisper.onPress?.();
          dismissWhisper();
        }}
      >
        <View style={styles.row}>
          <View style={styles.copy}>
            <Text style={styles.title}>{whisper.title}</Text>
            <Text style={styles.body} numberOfLines={3}>
              {whisper.message}
            </Text>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Dismiss"
            hitSlop={8}
            onPress={dismissWhisper}
            style={styles.dismiss}
          >
            <Text style={styles.dismissText}>×</Text>
          </Pressable>
        </View>
      </GlassCard>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    left: spacing.md,
    right: spacing.md,
    zIndex: 80,
  },
  row: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  copy: { flex: 1, gap: 4 },
  title: { ...typography.caption, fontWeight: '800', color: colors.text },
  body: { ...typography.caption, color: colors.textMuted, lineHeight: 18 },
  dismiss: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dismissText: { color: colors.textMuted, fontSize: 22, lineHeight: 24, fontWeight: '600' },
});
